import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { displayMoney, minus, add, Money } from '../../../../utils/money';
import CustomDialog from '../../../common/custom-dialog/custom-dialog';
import {
  CircularProgress,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Typography,
  Box,
  useTheme,
  Stack,
  Tooltip,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import InfoIcon from '@mui/icons-material/Info';
import { user_is_merchant } from '../../../../utils/permissions';

type OrderType = 'STANDARD' | 'RETURN';

type MerchantCategoryGroup = {
  price?: Money[];
  delivery_fee?: Money[];
  pop?: Money[];
};

type InvoiceCategory =
  | 'pending_invoice_preparation'
  | 'invoice_prepared'
  | 'payment_in_progress';

type MerchantCollectionData = Partial<
  Record<OrderType, Partial<Record<InvoiceCategory, MerchantCategoryGroup>>>
>;

interface MerchantDataV2 {
  collected: MerchantCollectionData;
  pending_collection: MerchantCollectionData;
}

interface MerchantBalanceV2 {
  merchant_id: number;
  merchant_name: string;
  data: MerchantDataV2;
}

interface ApiResponse {
  collected: MerchantBalanceV2[];
  pending_collection: MerchantBalanceV2[];
}

interface MerchantsBalanceProps {
  onClose: () => void;
  title: string;
}

const calculateTotalAmount = (
  balance?: MerchantCategoryGroup,
  orderType?: OrderType
): Money[] => {
  if (!balance) return [];

  const price: Money[] = balance.price ?? [];
  const deliveryFee: Money[] = balance.delivery_fee ?? [];
  const pop: Money[] = balance.pop ?? [];

  if (orderType === 'RETURN') {
    return add(price, deliveryFee);
  }
  return minus(price, pop);
};

const calculateCategoryTotal = (
  data: MerchantDataV2,
  collectionType: keyof MerchantDataV2,
  orderType: OrderType,
  category: InvoiceCategory
): Money[] => {
  const categoryData = data[collectionType]?.[orderType]?.[category];
  return calculateTotalAmount(categoryData, orderType);
};

const calculateCollectionSubtotal = (
  data: MerchantDataV2,
  collectionType: keyof MerchantDataV2,
  orderType: OrderType
): Money[] => {
  const categories: InvoiceCategory[] = [
    'pending_invoice_preparation',
    'invoice_prepared',
    'payment_in_progress',
  ];

  let result: Money[] = [];
  categories.forEach((category) => {
    const amount = calculateCategoryTotal(
      data,
      collectionType,
      orderType,
      category
    );
    result = add(result, amount);
  });

  return result;
};

const calculateCollectionTotal = (
  data: MerchantDataV2,
  collectionType: keyof MerchantDataV2
): Money[] => {
  const standardTotal = calculateCollectionSubtotal(
    data,
    collectionType,
    'STANDARD'
  );
  const returnTotal = calculateCollectionSubtotal(
    data,
    collectionType,
    'RETURN'
  );
  return minus(standardTotal, returnTotal);
};

const calculateMerchantTotal = (data: MerchantDataV2): Money[] => {
  const collectedTotal = calculateCollectionTotal(data, 'collected');
  const pendingTotal = calculateCollectionTotal(data, 'pending_collection');
  return add(collectedTotal, pendingTotal);
};

const OrderTypeRow: React.FC<{
  orderType: OrderType;
  data: MerchantDataV2;
  isLast: boolean;
}> = ({ orderType, data, isLast }) => {
  const theme = useTheme();
  const categories: InvoiceCategory[] = [
    'pending_invoice_preparation',
    'invoice_prepared',
    'payment_in_progress',
  ];

  return (
    <TableRow
      sx={{
        backgroundColor: theme.palette.grey[100],
        '&:last-child td': { borderBottom: isLast ? 'none' : undefined },
      }}
    >
      <TableCell sx={{ fontWeight: 500 }}>
        {orderType === 'STANDARD' ? 'Standard Orders' : 'Return Orders'}
      </TableCell>
      <TableCell align="center" sx={{ fontWeight: 500 }}>
        {displayMoney(
          calculateCollectionSubtotal(data, 'pending_collection', orderType)
        )}
      </TableCell>
      {categories.map((category) => (
        <TableCell key={category} align="center" sx={{ fontWeight: 500 }}>
          {displayMoney(
            calculateCategoryTotal(data, 'collected', orderType, category)
          )}
        </TableCell>
      ))}
      <TableCell align="center" sx={{ fontWeight: 600 }}>
        {displayMoney(
          add(
            calculateCollectionSubtotal(data, 'pending_collection', orderType),
            calculateCollectionSubtotal(data, 'collected', orderType)
          )
        )}
      </TableCell>
    </TableRow>
  );
};

const MerchantRow: React.FC<{
  merchant: MerchantBalanceV2;
  isLast: boolean;
  defaultIsOpen?: boolean;
}> = ({ merchant, isLast, defaultIsOpen }) => {
  const [open, setOpen] = useState(defaultIsOpen);
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  const calculateCombinedCategoryTotal = (
    category: InvoiceCategory
  ): Money[] => {
    const standard = calculateCategoryTotal(
      merchant.data,
      'collected',
      'STANDARD',
      category
    );
    const returns = calculateCategoryTotal(
      merchant.data,
      'collected',
      'RETURN',
      category
    );
    return minus(standard, returns);
  };

  return (
    <>
      <TableRow
        hover
        sx={{
          '& > td': { borderBottom: open ? 'none' : undefined },
          '&:last-child td': { borderBottom: isLast ? 'none' : undefined },
        }}
      >
        <TableCell>
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
            sx={{
              color: theme.palette.primary.main,
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{merchant.merchant_name}</TableCell>
        <TableCell align="center" sx={{ fontWeight: 600 }}>
          {displayMoney(
            calculateCollectionTotal(merchant.data, 'pending_collection')
          )}
        </TableCell>
        <TableCell align="center" sx={{ fontWeight: 600 }}>
          {displayMoney(calculateCollectionTotal(merchant.data, 'collected'))}
        </TableCell>
        <TableCell align="center" sx={{ fontWeight: 700 }}>
          {displayMoney(calculateMerchantTotal(merchant.data))}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={6}
          sx={{
            p: 0,
            borderBottom: isLast ? 'none' : undefined,
            backgroundColor: theme.palette.grey[50],
          }}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 2 }}>
              <Table size="small" sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '20%' }}>Service</TableCell>
                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography>Pending Collection</Typography>
                        {user_is_merchant(user) && (
                          <Tooltip title="Amounts that are yet to be collected from your clients by our delivery driver. Orders that have not been picked up yet are not included.">
                            <InfoIcon color="primary" fontSize="small" />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography>Pending Preparation</Typography>
                        {user_is_merchant(user) && (
                          <Tooltip title="Amounts collected by our drivers and being prepared to hand back to you.">
                            <InfoIcon color="primary" fontSize="small" />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography>Invoice Prepared</Typography>
                        {user_is_merchant(user) && (
                          <Tooltip title="Your invoice has been prepared and the amount will be paid to you soon!">
                            <InfoIcon color="primary" fontSize="small" />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Typography>Payment In Progress</Typography>
                        {user_is_merchant(user) && (
                          <Tooltip title="Our driver is on the way to pay you this amount!">
                            <InfoIcon color="primary" fontSize="small" />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(['STANDARD', 'RETURN'] as OrderType[]).map(
                    (orderType, i) => (
                      <OrderTypeRow
                        key={orderType}
                        orderType={orderType}
                        data={merchant.data}
                        isLast={i === 1}
                      />
                    )
                  )}
                  <TableRow
                    sx={{
                      backgroundColor: theme.palette.grey[200],
                      '& td': { fontWeight: 700 },
                    }}
                  >
                    <TableCell>Total</TableCell>
                    <TableCell align="center">
                      {displayMoney(
                        calculateCollectionTotal(
                          merchant.data,
                          'pending_collection'
                        )
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {displayMoney(
                        calculateCombinedCategoryTotal(
                          'pending_invoice_preparation'
                        )
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {displayMoney(
                        calculateCombinedCategoryTotal('invoice_prepared')
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {displayMoney(
                        calculateCombinedCategoryTotal('payment_in_progress')
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {displayMoney(calculateMerchantTotal(merchant.data))}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

function MerchantsBalance({ onClose, title }: MerchantsBalanceProps) {
  const { config } = useContext(AuthContext);
  const [balances, setBalances] = useState<MerchantBalanceV2[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const theme = useTheme();

  const calculateGrandTotals = (balances: MerchantBalanceV2[]) => {
    let pendingTotal: Money[] = [];
    let collectedTotal: Money[] = [];
    let balanceTotal: Money[] = [];

    balances.forEach((merchant) => {
      pendingTotal = add(
        pendingTotal,
        calculateCollectionTotal(merchant.data, 'pending_collection')
      );
      collectedTotal = add(
        collectedTotal,
        calculateCollectionTotal(merchant.data, 'collected')
      );
      balanceTotal = add(balanceTotal, calculateMerchantTotal(merchant.data));
    });

    return { pendingTotal, collectedTotal, balanceTotal };
  };

  const { user } = useContext(AuthContext);

  useEffect(() => {
    const fetchBalances = async () => {
      try {
        const endpoint = `${API_ENDPOINT}/api/v2/merchants-balance/`;
        const response = await axios.get<ApiResponse>(endpoint, config);

        const allMerchantIds = new Set<number>([
          ...response.data.collected.map((m) => m.merchant_id),
          ...response.data.pending_collection.map((m) => m.merchant_id),
        ]);

        const combinedData = Array.from(allMerchantIds).map((merchantId) => {
          const collectedItem = response.data.collected.find(
            (m) => m.merchant_id === merchantId
          );
          const pendingItem = response.data.pending_collection.find(
            (m) => m.merchant_id === merchantId
          );

          return {
            merchant_id: merchantId,
            merchant_name:
              collectedItem?.merchant_name ||
              pendingItem?.merchant_name ||
              'Unknown',
            data: {
              collected: collectedItem?.data || {},
              pending_collection: pendingItem?.data || {},
            },
          };
        });

        setBalances(combinedData);
      } catch (error) {
        console.error('Failed to fetch merchant balances:', error);
      }
      setIsLoading(false);
    };

    fetchBalances();
  }, [config]);

  return (
    <CustomDialog open={true} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          py: 2,
        }}
      >
        <Typography
          variant="h5"
          component="div"
          align="center"
          fontWeight={600}
        >
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ borderRadius: 0, boxShadow: 'none' }}
          >
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: theme.palette.grey[200],
                    '& th': { fontWeight: 700, py: 2 },
                  }}
                >
                  <TableCell sx={{ width: 60 }}></TableCell>
                  <TableCell>Merchant</TableCell>
                  <TableCell align="center">Pending Collection</TableCell>
                  <TableCell align="center">Collected</TableCell>
                  <TableCell align="center">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {balances.length > 1 && (
                  <TableRow sx={{ backgroundColor: theme.palette.grey[300] }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                      Total Across All Merchants
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      {displayMoney(
                        calculateGrandTotals(balances).pendingTotal
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      {displayMoney(
                        calculateGrandTotals(balances).collectedTotal
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800 }}>
                      {displayMoney(
                        calculateGrandTotals(balances).balanceTotal
                      )}
                    </TableCell>
                  </TableRow>
                )}

                {balances.map((merchant, index) => (
                  <MerchantRow
                    key={merchant.merchant_id}
                    merchant={merchant}
                    defaultIsOpen={balances.length === 1}
                    isLast={index === balances.length - 1}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </CustomDialog>
  );
}

export default MerchantsBalance;
